// Image preloading utility to improve user experience in tables and common areas

const imageCache = new Map<string, Promise<HTMLImageElement>>();

/**
 * Preload an image and cache the promise
 * @param src - Image source URL
 * @returns Promise that resolves when image is loaded
 */
export const preloadImage = (src: string): Promise<HTMLImageElement> => {
  if (imageCache.has(src)) {
    return imageCache.get(src)!;
  }

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

/**
 * Preload multiple images concurrently
 * @param sources - Array of image source URLs
 * @returns Promise that resolves when all images are loaded
 */
export const preloadImages = async (sources: string[]): Promise<void> => {
  try {
    await Promise.all(sources.map((src) => preloadImage(src)));
  } catch (error) {
    console.warn('Some images failed to preload:', error);
  }
};

/**
 * Check if an image is already cached/preloaded
 * @param src - Image source URL
 * @returns boolean indicating if image is cached
 */
export const isImageCached = (src: string): boolean => {
  return imageCache.has(src);
};

/**
 * Clear the image cache (useful for memory management)
 */
export const clearImageCache = (): void => {
  imageCache.clear();
};

/**
 * Preload common project type icons that are frequently used in tables
 * This should be called early in the app lifecycle
 */
export const preloadCommonProjectIcons = async (assetBaseUrl: string): Promise<void> => {
  // Common project type logos that appear frequently in tables
  const commonLogos = [
    'afforestation_and_reforestation.svg',
    'improved_forest_management.svg',
    'agriculture_land_management.svg',
    'biochar.svg',
    'household_biodigesters.svg',
    'manure_management.svg',
    'cookstoves.svg',
    'landfill_gas.svg',
    'waste_to_energy.svg',
    'solar.svg',
    'wind.svg',
    'hydroelectric.svg',
  ];

  const imageSources = commonLogos.map((logo) => `${assetBaseUrl}/${logo}`);
  await preloadImages(imageSources);
};
